/**
 * Shared vocabulary for the content-refresh review UI.
 *
 * Both BlogDiffPanel (what actually changed) and RefreshRationale (what the AI
 * says it changed) read from here so the two views can never drift apart on
 * field names, labels or ordering.
 *
 * Mirrors REFRESH_CHANGE_TYPES in
 * firebase/functions/services/blog/firestoreOps.js — keep the two in sync.
 */

/**
 * Meta fields compared side-by-side on a refresh.
 *
 * `content` is deliberately absent: it needs line/word-level diffing rather
 * than the value-vs-value rendering this drives.
 */
export const META_FIELDS = [
  { key: "title",            label: "Title" },
  { key: "description",      label: "Meta description" },
  { key: "readTime",         label: "Read time" },
  { key: "emoji",            label: "Emoji" },
  { key: "tags",             label: "Tags" },
  { key: "targetedKeywords", label: "Targeted keywords" },
  { key: "keywordCluster",   label: "Keyword cluster" },
];

/** Presentation for each AI-reported change category. */
export const CHANGE_TYPE_META = {
  gap_coverage:   { label: "Gap coverage",  icon: "🎯", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  stale_fact:     { label: "Stale fact",    icon: "📅", className: "bg-amber-50 text-amber-700 border-amber-200" },
  ctr:            { label: "CTR",           icon: "📈", className: "bg-sky-50 text-sky-700 border-sky-200" },
  depth:          { label: "Depth",         icon: "📝", className: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  internal_links: { label: "Internal links",icon: "🔗", className: "bg-purple-50 text-purple-700 border-purple-200" },
  structure:      { label: "Structure",     icon: "🧱", className: "bg-slate-50 text-slate-700 border-slate-200" },
  other:          { label: "Other",         icon: "•",  className: "bg-gray-50 text-gray-600 border-gray-200" },
};

export function changeTypeMeta(type) {
  return CHANGE_TYPE_META[type] || CHANGE_TYPE_META.other;
}

/**
 * Normalise a meta value to a comparable string.
 * Tags and targetedKeywords arrive as either arrays or comma-joined strings
 * depending on whether the post came from WordPress meta or Gemini JSON.
 */
export function metaValueToString(value) {
  if (value == null) return "";
  if (Array.isArray(value)) return value.filter(Boolean).join(", ");
  return String(value).trim();
}

/**
 * Reduce HTML to comparable plain text, one block element per line.
 *
 * The refresh baseline is WordPress-RENDERED HTML while the new version is
 * Gemini's semantic HTML. Diffing those raw would flag every paragraph as
 * changed on markup alone — WP's autop, entity encoding and attribute ordering
 * differ from the model's output even when the prose is identical. Stripping
 * both sides to text means the diff reflects editorial change only.
 *
 * Block boundaries become newlines so diffLines() still has paragraph-level
 * units to align on rather than one unbroken string.
 */
export function htmlToComparableText(html) {
  if (!html) return "";

  return String(html)
    // Drop non-prose elements entirely, including their content.
    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, "")
    // Block-level boundaries → newline, so paragraphs stay separate lines.
    .replace(/<\/(p|div|h[1-6]|li|tr|blockquote|section|article|figure|table|ul|ol)\s*>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    // Remaining tags carry no prose.
    .replace(/<[^>]+>/g, "")
    // Decode the entities WP and the editor emit; &amp; last so "&amp;lt;"
    // does not decode twice into a bare "<".
    .replace(/&nbsp;/gi, " ")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#0?39;|&apos;|&#x27;/gi, "'")
    .replace(/&#8217;|&rsquo;/gi, "\u2019")
    .replace(/&#8216;|&lsquo;/gi, "\u2018")
    .replace(/&#822[01];|&[lr]dquo;/gi, '"')
    .replace(/&#8211;|&ndash;/gi, "\u2013")
    .replace(/&#8212;|&mdash;/gi, "\u2014")
    .replace(/&amp;/gi, "&")
    // Collapse intra-line whitespace, trim each line, drop blank runs.
    .split("\n")
    .map((line) => line.replace(/[ \t\u00a0]+/g, " ").trim())
    .filter((line) => line.length > 0)
    .join("\n");
}
